import { useEffect, useState } from 'react';
import { createContainer } from 'react-tracked';
import { id } from '../../../../constants';
import { useBackendApi } from '../../../../helpers/backendHelper';
import { useCosmos } from '../../../../helpers/cosmosHelper';
import { useMintHelper } from '../../../../helpers/mintHelper';
import utils from '../../../../utils';
import { useEvent } from '../EventProvider';
import ConnectWallet from './ConnectWallet';
import SetUsername from './SetUsername';
import Step1 from './Step1';
import StepLast from './StepLast';
import StepMintPreview from './StepMintPreview';

const useMintState = () => {
  const [state, setState] = useState({});
  const [{ communityName, campaign, nftProfile, refreshNftProfile }] = useEvent();
  const backendHelper = useBackendApi();
  const mintHelper = useMintHelper();
  const [{ xionBalance }] = useCosmos();

  const dynamicFee = campaign?.mintFeeConfig?.dynamicPriceOnUsername;

  const _getDynamicFee = async (name, minFee, maxFee, exponentialValue) => {
    let fee;
    if (dynamicFee) {
      const len = name?.length;
      if (!minFee || !maxFee || !exponentialValue) return;
      if (!len) return maxFee;
      const weighted = await checkWeighted(name);
      if (weighted) {
        fee = weighted;
      } else {
        const round = Math.max(minFee?.toString()?.split('.')?.[1]?.length || 0, 2);
        fee = Math.max(maxFee * Math.exp(exponentialValue * (len - 1)), minFee).toFixed(round);
      }
    } else {
      fee = campaign?.mintFeeConfig?.amount;
    }
    // return Math.max(maxFee * Math.exp(exponentialValue * (len - 1)), minFee).toFixed(round);
    setState(s => ({ ...s, mintFee: fee || 0 }));
  };

  // const debouncedGetDynamicFee = debounce(_getDynamicFee, 1000);

  const suffix = campaign?.usernameSuffix;

  const chainDetails = utils.getChainDetails(campaign?.chainId);

  const chainName = chainDetails?.name;
  const feeToken = chainDetails?.nativeCurrency?.symbol;
  const isDisabled = nftProfile || campaign?.hasEnded;

  const isCosmos = campaign?.isCosmos;

  const cosmosHelper = useCosmos();

  const usernameFull = `${state.username}.${suffix}`;

  /**
   * todo: handle email verification if email is not connected, also check if is whitelisted
   */

  useEffect(() => {
    const steps = [<Step1 />, <SetUsername />, <StepLast />, <StepMintPreview />];
    const step = 0;
    if (isCosmos) {
      if (!cosmosHelper[0]?.cosmosLoggedIn) steps.unshift(<ConnectWallet />);
    }

    setState(s => ({ ...s, steps, step }));
  }, [cosmosHelper[0]?.cosmosLoggedIn, isCosmos]);

  useEffect(() => {
    window.addEventListener('logout', logout);
  }, []);

  const onNext = () => {
    if (isDisabled) return alert.error('Nft profile already exists');

    setState(s => ({ ...s, step: Math.min(s.step + 1, s.steps.length - 1) }));
  };

  const onPrevious = () => {
    if (isDisabled) return alert.error('Nft profile already exists');

    setState(s => ({ ...s, step: Math.max(s.step - 1, 0) }));
  };

  const checkWeighted = async userName => {
    try {
      const weight = await backendHelper.user.getUsernameWeights(userName);
      if (weight) {
        return +dynamicFee?.maxFee;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const setUsername = t => {
    const value = t.target.value;
    const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '');
    const limitedValue = filteredValue.slice(0, 16);
    setState(s => ({ ...s, username: limitedValue }));
    // _getDynamicFee(limitedValue, dynamicFee?.minFee, dynamicFee?.maxFee, dynamicFee?.exponential);
  };

  const saveUsername = async (referralCode) => {
    if (!state.username) return alert.error('Name required');
    // if (+xionBalance < +mintFee) return onNext();
    _getDynamicFee(state.username, dynamicFee?.minFee, dynamicFee?.maxFee, dynamicFee?.exponential);
    if (!(await mintHelper.setUsername(usernameFull))) return;

    // Save referral code to state if provided
    const refCode = referralCode || localStorage.getItem('refCode');
    if (refCode) {
      setState(s => ({ ...s, referralCode: refCode }));
      // Also save to localStorage for consistency
      localStorage.setItem('refCode', refCode);
    }

    onNext();
  };

  const mint = async () => {
    if (!state.username) return alert.error('Username missing');
    
    const referralCode = localStorage.getItem('refCode');
    console.log('Minting with referral code:', referralCode);
    
    // perform validations and then proceed with minting
    let mintResult;
    if (isCosmos) {
      mintResult = await cosmosHelper[0]?.mintCosmosNFT(communityName, campaign, usernameFull, referralCode);
    } else {
      mintResult = await mintHelper.mint(communityName, campaign, usernameFull, referralCode, refreshNftProfile);
    }

    // If minting was successful and we have a valid referral code, call the useReferralCode API
    if (referralCode && referralCode.trim() !== '') {
      try {
        console.log('Calling useXionReferralCode API with:', { referralCode });
        await backendHelper.cosmos.useXionReferralCode({ referralCode });
        console.log('Referral code used successfully:', referralCode);
        localStorage.removeItem('refCode'); // Clear the referral code after successful use
      } catch (error) {
        console.error('Error using referral code:', error);
        // Don't show error to user as minting was successful
      }
    } else {
      console.log('skipped usecode call ref code not found in local storage or ref code is empty');
    }

    return mintResult;
  };

  const logout = () => {
    setState(s => ({ ...s, username: undefined, step: 0 }));
  };

  return [{ isDisabled, chainName, suffix, feeToken, ...state, setUsername, saveUsername, mint, onNext, onPrevious }, setState];
};

const { Provider, useTracked } = createContainer(() => useMintState());

export const useMint = useTracked;
export const MintProvider = ({ children }) => <Provider>{children}</Provider>;
export default MintProvider;
